// Inline confirmation card: previews every requested write action with per-action
// Approve/Reject and batch "Alle genehmigen" / "Alle ablehnen" controls.

import { useState } from "react";
import type { ToolCallView } from "../../stores/ai.store";

interface ConfirmCardProps {
  pending: ToolCallView[];
  onApproveAll: () => void;
  onRejectAll: () => void;
  onSubmit: (decisions: Array<{ toolCallId: string; approved: boolean }>) => void;
}

export function ConfirmCard({ pending, onApproveAll, onRejectAll, onSubmit }: ConfirmCardProps) {
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const single = pending.length === 1;

  const setOne = (id: string, approved: boolean) => setSelected((prev) => ({ ...prev, [id]: approved }));
  const allApproved = pending.every((p) => selected[p.toolCallId] === true);
  const allRejected = pending.every((p) => selected[p.toolCallId] === false);
  const submit = () => onSubmit(pending.map((p) => ({ toolCallId: p.toolCallId, approved: selected[p.toolCallId] ?? false })));

  return (
    <div className="my-1 w-full rounded-xl border border-red-500/50 bg-surface-2 p-2.5">
      <div className="mb-1.5 font-bold text-red-300">{single ? "Aktion bestätigen" : "Aktionen bestätigen"}</div>
      {pending.map((p) => {
        const choice = selected[p.toolCallId];
        return (
          <div
            key={p.toolCallId}
            className={`my-1 flex flex-wrap items-center justify-between gap-2 rounded-lg border p-1.5 ${
              p.danger ? "border-red-500/60" : "border-line"
            }`}
          >
            <div className={`font-mono text-xs ${p.danger ? "text-red-300" : "text-fg"}`}>{p.summary}</div>
            <div className="flex gap-1.5">
              <button
                onClick={() => setOne(p.toolCallId, true)}
                className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                  choice === true ? "border-green-500 bg-green-700 text-green-50" : "border-line bg-surface text-fg hover:bg-white/5"
                }`}
              >
                Genehmigen
              </button>
              <button
                onClick={() => setOne(p.toolCallId, false)}
                className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                  choice === false ? "border-red-500 bg-red-800 text-red-50" : "border-line bg-surface text-fg hover:bg-white/5"
                }`}
              >
                Ablehnen
              </button>
            </div>
          </div>
        );
      })}
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        <button
          onClick={onApproveAll}
          disabled={allApproved}
          className="rounded-md border border-green-500/60 bg-green-700/80 px-2 py-1 text-xs text-green-50 hover:bg-green-700 disabled:opacity-40"
        >
          {single ? "Genehmigen" : "Alle genehmigen"}
        </button>
        <button
          onClick={onRejectAll}
          disabled={allRejected}
          className="rounded-md border border-red-500/60 bg-red-800/80 px-2 py-1 text-xs text-red-50 hover:bg-red-800 disabled:opacity-40"
        >
          {single ? "Ablehnen" : "Alle ablehnen"}
        </button>
        <button
          onClick={submit}
          className="rounded-md border border-ember bg-ember/20 px-2 py-1 text-xs font-semibold text-ember hover:bg-ember/30"
        >
          Absenden
        </button>
      </div>
    </div>
  );
}



